import * as d3 from 'd3';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { codeToCountryName } from '../utils/completeCountryMapping';

const StreamGraph = ({ region, data, ipType = 'ipv4', globalDate, selectedTimestamp, showTTonMouse = false }) => {
    const svgRef = useRef();
    const containerRef = useRef();
    const [dimensions, setDimensions] = useState({ width: 800, height: 400 });
    const [hoveredCountry, setHoveredCountry] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState(null);

    // Function to update dimensions based on the container
    const updateDimensions = useCallback(() => {
        if (containerRef.current) {
            const { offsetWidth: containerWidth } = containerRef.current;
            const newWidth = containerWidth; // Usar el ancho completo del contenedor
            const newHeight = Math.max(300, newWidth * 0.5); // Ratio 2:1

            setDimensions({ width: newWidth, height: newHeight });
        }
    }, []);

    // Listen for window size changes
    useEffect(() => {
        updateDimensions();

        const handleResize = () => {
            updateDimensions();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [updateDimensions]);

    // Calculate responsive margins
    const margin = useMemo(() => {
        const baseMargin = { top: 0, right: 0, bottom: 30, left: 0 };
        // Only adjust bottom margins on small screens
        const { width } = dimensions;
        if (width < 600) {
            baseMargin.bottom = 50;
        }
        return baseMargin;
    }, [dimensions]);

    const { width, height } = dimensions;
    const { top, right, bottom, left } = margin;
    const chartWidth = width - left - right;
    const chartHeight = height - top - bottom;

    // Clear selection when region changes
    useEffect(() => {
        setSelectedCountry(null);
        setHoveredCountry(null);
    }, [region]);

    useEffect(() => {
        if (!data || !data[region]) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        // Process region data
        const regionData = data[region];

        // Get all unique timestamps and sort them
        const allTimestamps = new Set();
        Object.values(regionData).forEach(countryData => {
            if (countryData[ipType]) {
                Object.keys(countryData[ipType]).forEach(timestamp => {
                    allTimestamps.add(parseInt(timestamp));
                });
            }
        });

        const timestamps = Array.from(allTimestamps).sort((a, b) => a - b);
        if (timestamps.length === 0) return;

        // Get countries with significant data using the original logic
        const countryTotals = {};
        Object.entries(regionData).forEach(([countryCode, countryData]) => {
            if (countryData[ipType]) {
                let total = 0;
                Object.values(countryData[ipType]).forEach(value => {
                    total += parseFloat(value) || 0;
                });
                if (total > 0) {
                    countryTotals[countryCode] = total;
                }
            }
        });

        // Select countries to show - dynamic limit based on region size

        const topCountries = Object.entries(countryTotals)
            .sort((a, b) => b[1] - a[1])
            .map(([code]) => code);

        if (topCountries.length === 0) return;

        // Construir dataset para el streamgraph
        const dataset = timestamps.map(timestamp => {
            const dataPoint = { timestamp };

            topCountries.forEach(countryCode => {
                const countryData = regionData[countryCode];
                let value = 0;

                if (countryData && countryData[ipType]) {
                    // Find the closest value up to this date
                    const countryTimestamps = Object.keys(countryData[ipType])
                        .map(t => parseInt(t))
                        .filter(t => t <= timestamp)
                        .sort((a, b) => b - a);

                    if (countryTimestamps.length > 0) {
                        value = parseFloat(countryData[ipType][countryTimestamps[0]]) || 0;
                    }
                }

                dataPoint[countryCode] = value;
            });

            return dataPoint;
        });

        // Configurar escalas
        const xScale = d3.scaleTime()
            .domain(d3.extent(timestamps, d => new Date(d * 1000)))
            .range([0, chartWidth]);

        // Crear stack
        const stack = d3.stack()
            .keys(topCountries)
            .offset(d3.stackOffsetWiggle)
            .order(d3.stackOrderInsideOut);

        const stackedData = stack(dataset);

        // Configurar escala Y basada en los datos apilados
        const yScale = d3.scaleLinear()
            .domain(d3.extent(stackedData.flat(2)))
            .range([chartHeight, 0]);

        // Configurar colores usando los colores importados
        const colorScale = d3.scaleOrdinal()
            .domain(topCountries)
            .range(d3.schemeTableau10);

        // Create area generator
        const area = d3.area()
            .x((d, i) => xScale(new Date(d.data.timestamp * 1000)))
            .y0(d => yScale(d[0]))
            .y1(d => yScale(d[1]))
            .curve(d3.curveBasis);

        // Crear contenedor principal
        const g = svg.append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);


        // Draw streamgraph areas
        const paths = g.selectAll("path")
            .data(stackedData)
            .enter().append("path")
            .attr("d", area)
            .attr("fill", d => colorScale(d.key))
            .attr("opacity", d => selectedCountry === d.key ? 1.0 : 0.4) // Selected country without transparency
            .attr("stroke", "#2d374888")
            .attr("stroke-width", 1)
            .style("cursor", "pointer");

        // Add mouse events if showTTonMouse is enabled
        if (showTTonMouse) {
            paths
                .on("mouseenter", function (event, d) {
                    // Always allow hover to show tooltips
                    setHoveredCountry(d.key);

                    // Change opacity: hovered country = 0.8, selected = 1.0, others = 0.1
                    paths.transition().duration(200)
                        .attr("opacity", pathData => {
                            if (pathData.key === selectedCountry) return 1.0; // Selected maintains full opacity
                            if (pathData.key === d.key) return 0.8; // Hover
                            return 0.1; // Others dimmed
                        });
                })
                .on("mouseleave", function (event, d) {
                    // Limpiar hover
                    setHoveredCountry(null);

                    // Restaurar opacidad: seleccionado = 1.0, otros = 0.4
                    paths.transition().duration(200)
                        .attr("opacity", pathData => selectedCountry === pathData.key ? 1.0 : 0.4);
                })
                .on("click", function (event, d) {
                    // Toggle selection: if already selected, deselect
                    if (selectedCountry === d.key) {
                        setSelectedCountry(null);
                        setHoveredCountry(null);
                        // Restore normal opacity for all
                        paths.transition().duration(200)
                            .attr("opacity", 0.4);
                    } else {
                        // Select new country (automatically deselects the previous one)
                        setSelectedCountry(d.key);
                        setHoveredCountry(null);
                        // Highlight selected country with full opacity
                        paths.transition().duration(200)
                            .attr("opacity", pathData => pathData.key === d.key ? 1.0 : 0.4);
                    }
                });
        }

        // New logic for vertical line and intersections
        if (globalDate) {
            const globalTimestamp = Math.floor(globalDate.getTime() / 1000);
            if (globalTimestamp >= timestamps[0] && globalTimestamp <= timestamps[timestamps.length - 1]) {
                const xPos = xScale(globalDate);

                // Draw the red line
                g.append("line")
                    .attr("x1", xPos)
                    .attr("x2", xPos)
                    .attr("y1", 0)
                    .attr("y2", chartHeight)
                    .attr("stroke", "#e74c3c")
                    .attr("stroke-width", 2)
                    .attr("opacity", 0.8);

                // Find precise intersections
                const intersections = [];

                stackedData.forEach(countryStack => {
                    const countryCode = countryStack.key;

                    // Find the two closest time points for interpolation
                    let beforePoint = null;
                    let afterPoint = null;

                    for (let i = 0; i < countryStack.length - 1; i++) {
                        const currentTime = countryStack[i].data.timestamp;
                        const nextTime = countryStack[i + 1].data.timestamp;

                        if (currentTime <= globalTimestamp && nextTime >= globalTimestamp) {
                            beforePoint = countryStack[i];
                            afterPoint = countryStack[i + 1];
                            break;
                        }
                    }

                    if (beforePoint && afterPoint) {
                        // Interpolate the exact Y position at globalTimestamp time
                        const t = (globalTimestamp - beforePoint.data.timestamp) /
                            (afterPoint.data.timestamp - beforePoint.data.timestamp);

                        const y0 = beforePoint[0] + t * (afterPoint[0] - beforePoint[0]);
                        const y1 = beforePoint[1] + t * (afterPoint[1] - beforePoint[1]);
                        const yCenter = yScale((y0 + y1) / 2);

                        // Calculate interpolated value for the streamgraph (smoothed)
                        const value = beforePoint.data[countryCode] + t *
                            (afterPoint.data[countryCode] - beforePoint.data[countryCode]);

                        // Get real value from original data for tooltip
                        let realValue = 0;
                        const countryData = regionData[countryCode];
                        if (countryData && countryData[ipType]) {
                            // Find the closest value up to this date in the original data
                            const countryTimestamps = Object.keys(countryData[ipType])
                                .map(t => parseInt(t))
                                .filter(t => t <= globalTimestamp)
                                .sort((a, b) => b - a);

                            if (countryTimestamps.length > 0) {
                                realValue = parseFloat(countryData[ipType][countryTimestamps[0]]) || 0;
                            }
                        }

                        // Calculate total for percentages
                        let total = 0;
                        topCountries.forEach(country => {
                            const countVal = beforePoint.data[country] + t *
                                (afterPoint.data[country] - beforePoint.data[country]);
                            total += countVal;
                        });

                        const percentage = total > 0 ? (value / total) * 100 : 0;

                        // In showTTonMouse mode, add all countries to be able to show tooltip
                        // En modo normal, solo los que superan el 3%
                        const shouldInclude = showTTonMouse ? true : (percentage > 3);

                        if (shouldInclude) {
                            intersections.push({
                                country: countryCode,
                                name: codeToCountryName[countryCode] || countryCode,
                                x: xPos,
                                y: yCenter,
                                value: value,
                                realValue: realValue, // Valor real de los datos originales
                                percentage: percentage,
                                visible: showTTonMouse ?
                                    (selectedCountry === countryCode || hoveredCountry === countryCode) :
                                    true
                            });
                        }
                    }
                });

                // Ordenar intersecciones por porcentaje descendente
                intersections.sort((a, b) => b.percentage - a.percentage);

                // Draw intersection points and SVG tooltips
                intersections.forEach((intersection) => {
                    // In showTTonMouse mode, only show tooltip if it's visible
                    if (showTTonMouse && !intersection.visible) {
                        return; // Skip este tooltip
                    }

                    // Prepare tooltip text to calculate dimensions
                    const tooltipText = `${intersection.name}: ${Math.round(intersection.realValue).toLocaleString()}`;

                    // Crear un elemento temporal para medir el texto
                    const tempText = g.append("text")
                        .attr("font-size", "10px")
                        .attr("font-family", "Arial, sans-serif")
                        .text(tooltipText)
                        .style("visibility", "hidden");

                    // Obtener dimensiones del texto
                    const textBBox = tempText.node().getBBox();
                    tempText.remove(); // Limpiar elemento temporal

                    // Configure positions for tooltips with automatic height and padding
                    const horizontalPadding = 5;
                    const verticalPadding = 4;
                    const tooltipWidth = textBBox.width + (horizontalPadding * 2);
                    const tooltipHeight = textBBox.height + (verticalPadding * 2);
                    const tooltipOffset = 25; // Mitad de la distancia anterior (era 50)

                    let tooltipX, lineEndX, side;

                    // Determinar lado del tooltip (en coordenadas SVG)
                    if (intersection.x > chartWidth / 2) {
                        // Tooltip a la izquierda
                        tooltipX = intersection.x - tooltipOffset - tooltipWidth;
                        lineEndX = intersection.x - tooltipOffset;
                        side = 'left';
                    } else {
                        // Tooltip a la derecha
                        tooltipX = intersection.x + tooltipOffset;
                        lineEndX = intersection.x + tooltipOffset;
                        side = 'right';
                    }

                    intersection.tooltipX = tooltipX;
                    intersection.tooltipY = intersection.y - (tooltipHeight / 2); // Centrado verticalmente con el punto
                    intersection.lineEndX = lineEndX;
                    intersection.side = side;
                    intersection.tooltipWidth = tooltipWidth;
                    intersection.tooltipHeight = tooltipHeight;
                    intersection.tooltipText = tooltipText;
                });                // Collision resolution system (SVG coordinates)
                const resolveCollisions = (intersections) => {
                    let hasCollisions = true;
                    let iterations = 0;
                    const maxIterations = 100;

                    // Function to resolve collisions on one side
                    const resolveCollisionsForSide = (sideTooltips) => {
                        for (let i = 0; i < sideTooltips.length - 1; i++) {
                            const current = sideTooltips[i];
                            const next = sideTooltips[i + 1];

                            const currentBottom = current.tooltipY + current.tooltipHeight;
                            const nextTop = next.tooltipY;

                            if (currentBottom > nextTop - 1) { // Collision detected
                                hasCollisions = true;

                                // Calculate the necessary push
                                const overlap = currentBottom - nextTop + 1;
                                const pushAmount = overlap / 2;

                                // Empujar en direcciones opuestas
                                current.tooltipY -= pushAmount;
                                next.tooltipY += pushAmount;

                                // Check graph area limits (SVG coordinates)
                                const minY = 5;
                                const maxY = chartHeight - current.tooltipHeight - 5;

                                if (current.tooltipY < minY) {
                                    const excess = minY - current.tooltipY;
                                    current.tooltipY = minY;
                                    next.tooltipY += excess;
                                }

                                if (next.tooltipY + next.tooltipHeight > maxY) {
                                    const excess = (next.tooltipY + next.tooltipHeight) - maxY;
                                    next.tooltipY = maxY - next.tooltipHeight;
                                    current.tooltipY -= excess;
                                }
                            }
                        }
                    };

                    while (hasCollisions && iterations < maxIterations) {
                        hasCollisions = false;
                        iterations++;

                        // Separar tooltips por lado
                        const leftTooltips = intersections.filter(c => c.side === 'left').sort((a, b) => a.tooltipY - b.tooltipY);
                        const rightTooltips = intersections.filter(c => c.side === 'right').sort((a, b) => a.tooltipY - b.tooltipY);

                        // Resolver colisiones en cada lado
                        resolveCollisionsForSide(leftTooltips);
                        resolveCollisionsForSide(rightTooltips);
                    }

                    return intersections;
                };

                // Apply collision resolution
                const finalIntersections = resolveCollisions(intersections);

                // Create tooltips and connector lines with SVG
                finalIntersections.forEach((intersection) => {
                    // In showTTonMouse mode, only show elements if visible
                    if (showTTonMouse && !intersection.visible) {
                        return; // Skip este intersection
                    }

                    // Draw intersection point
                    g.append("circle")
                        .attr("cx", intersection.x)
                        .attr("cy", intersection.y)
                        .attr("r", 3)
                        .attr("fill", "#e74c3c")
                        .attr("stroke", "white")
                        .attr("stroke-width", 1.5);

                    // Calculate tooltip lateral center for connection
                    const tooltipCenterY = intersection.tooltipY + (intersection.tooltipHeight / 2);

                    // Connector line from point to tooltip lateral center
                    g.append("line")
                        .attr("x1", intersection.x)
                        .attr("y1", intersection.y)
                        .attr("x2", intersection.lineEndX)
                        .attr("y2", tooltipCenterY)
                        .attr("stroke", "#666")
                        .attr("stroke-width", 1)
                        .attr("stroke-dasharray", "2,2")
                        .attr("opacity", 0.7);

                    // Crear grupo para el tooltip SVG
                    const tooltipGroup = g.append("g")
                        .attr("class", "tooltip-group");

                    // Tooltip background (rectangle with rounded borders)
                    tooltipGroup.append("rect")
                        .attr("x", intersection.tooltipX)
                        .attr("y", intersection.tooltipY)
                        .attr("width", intersection.tooltipWidth)
                        .attr("height", intersection.tooltipHeight)
                        .attr("fill", "rgba(0, 0, 0, 0.7)")
                        .attr("stroke", "rgba(255, 255, 255, 0.3)")
                        .attr("stroke-width", 1)
                        .attr("rx", 3)
                        .attr("ry", 3);

                    // Texto del tooltip usando el texto pre-calculado
                    tooltipGroup.append("text")
                        .attr("x", intersection.tooltipX + intersection.tooltipWidth / 2)
                        .attr("y", intersection.tooltipY + intersection.tooltipHeight / 2 + 3) // +3 para centrar verticalmente
                        .attr("text-anchor", "middle")
                        .attr("fill", "white")
                        .attr("font-size", "10px")
                        .attr("font-family", "Arial, sans-serif")
                        .text(intersection.tooltipText);
                });
            }
        }

        // X axis with YY-MM format and rotation
        const xAxis = d3.axisBottom(xScale)
            .ticks(9)
            .tickFormat(d => {
                const date = new Date(d);
                return `${date.getFullYear().toString().slice(-2)}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
            });

        g.append("g")
            .attr("transform", `translate(0,${chartHeight})`)
            .call(xAxis)
            .selectAll("text")
            .style("text-anchor", "end")
            .style("font-size", "8px")
            .style("fill", "#666")
            .attr("dx", "-.8em")
            .attr("dy", ".15em")
            .attr("transform", "rotate(-45)");

        // X axis title




        // Cleanup function - ya no necesitamos limpiar divs HTML
        return () => {
            // SVG elements are automatically cleaned with svg.selectAll("*").remove()
        };

    }, [data, region, ipType, globalDate, chartHeight, chartWidth, dimensions, margin, selectedTimestamp, showTTonMouse, hoveredCountry, selectedCountry]);

    if (!data || !data[region]) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                No streamgraph data available for this region.
            </div>
        );
    }

    return (
        <div style={{
            marginTop: '30px',
            width: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden'
        }}>
            <h4 style={{
                margin: '0 0 15px 0',
                color: '#2d3748',
                fontSize: '1.1rem',
                fontWeight: '600'
            }}>
                Country Evolution Stream Graph
            </h4>
            <div
                ref={containerRef}
                style={{
                    width: '100%',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxSizing: 'border-box'
                }}
            >
                <svg
                    ref={svgRef}
                    style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        maxWidth: '100%'
                    }}
                    viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
                    preserveAspectRatio="xMidYMid meet"
                />
            </div>
        </div>
    );
};

export default StreamGraph;
